import { useMemo } from 'react';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { env } from '@eliadmin/core/src/env';
import { getCartQueryKey } from '@eliadmin/core/src/utils/cart-query-key';
import {
  fetchCart,
  type FetchCartRequest,
  type FetchCartResponse,
} from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';

export const useCartQueryOpts = () => {
  const locale = useLocaleStore(state => state.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const queryKey = useMemo(() => getCartQueryKey({ authToken: authToken, locale, currency, api: env.NEXT_PUBLIC_API_URL, store: env.NEXT_PUBLIC_CURRENT_STORE }), [authToken, locale, currency]);
  const req: FetchCartRequest = { locale, authToken };

  return {
    queryKey,
    enabled: !!authToken,
    queryFn: ({ signal }) => {
      if (!authToken) {
        throw new Error('Cart requests require an auth token.');
      }

      return fetchCart({ ...req, signal }).then(res => res.data);
    },
  } satisfies UseQueryOptions<FetchCartResponse['data']>;
};

export function useCart() {
  const opts = useCartQueryOpts();

  return useSuspenseQuery({
    queryKey: opts.queryKey,
    queryFn: opts.queryFn,
  });
}
